import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import CustomForm from "../../../custom/Forms/CustomForm";
import SelectInput from "../../../custom/components/Select-Input/SelectInput";
import TextInput from "../../../custom/components/Text-Input/TextInput";
import TextAreaInput from "../../../custom/components/TextArea-Input/TextAreaInput";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createPageData, basicFormData } from "./CategoryFile";
import CheckboxInput from "../../../custom/components/CheckBox-Input/CheckboxInput";
import { validate } from "../../../custom/validations/Validate";
import { getRecords, saveRecords } from "../../../services/api.service";
import { toast } from "react-toastify";

const CategoryCreate = () => {
  const schema = localStorage.getItem("schema");
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    payload,
    navigatePage,
    headerName,
    validationType,
    createEndpoint,
    updateEndpoint,
    getEndpoint,
  } = createPageData();

  const initialPayload = useMemo(() => payload, []);
  const [loadingData, setLoadingData] = useState(id ? true : false);
  const [formData, setFormData] = useState(basicFormData());
  const [formErrors, setFormErrors] = useState({});
  const [categoryNameError, setCategoryNameError] = useState(null)
  const [materialTypeError, setMaterialTypeError] = useState(null)

  const typeOptions = [
    { value: 0, label: "Material" },
    { value: 1, label: "Sparepart" },
  ];
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    if (id) {
      getEditData(id);
    }
  }, [id]);

  const getEditData = async (id) => {
    try {
      let payload = {
        modelName: "category_masters",
        schema: schema,
        whereCondition: { id: id },
      };

      const responseData = await getRecords(payload, getEndpoint);

      if (responseData?.data && responseData.data.length > 0) {
        const data = responseData.data[0];
        setFormData({ inputData: { ...data, material_type: data?.type } });
        const type =
          data?.type === 0
            ? { value: 0, label: "Material" }
            : { value: 1, label: "Sparepart" };
        setSelectedType(type);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoadingData(false);
    }
  };

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormErrors((prevErrors) => {
      const fieldError = validate(validationType, { [name]: value });
      return {
        ...prevErrors,
        [name]: fieldError[name] || null,
      };
    });

    setFormData((prevState) => ({
      ...prevState,
      inputData: {
        ...prevState.inputData,
        [name]:
          type === "checkbox"
            ? checked === true
              ? 1
              : 0
            : name === "alt_mobile_number" && value === ""
              ? null
              : value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setLoadingData(true);

      const userDetails = JSON.parse(localStorage.getItem("authUser"));
      const input = {
        ...formData.inputData,
        material_type: `${formData.inputData.material_type}`,
      };

      const validationErrors = validate(validationType, input);
      setFormErrors(validationErrors);
      if (JSON.stringify(validationErrors) !== "{}") return;

      let payload = {
        modelName: initialPayload?.modelName,
        schema: initialPayload?.schema,
      };

      if (id) {
        // update
        formData.inputData.updated_by = id ? userDetails?.id || "" : "";
        payload = {
          ...payload,
          id: id,
          inputData: formData?.inputData,
          relation: [],
        };
      } else {
        // create
        formData.inputData.created_by = userDetails?.id || "";
        formData.inputData.updated_by = userDetails?.id || "";
        payload = { ...payload, inputData: formData?.inputData, relation: [] };
      }
      const { material_type, ...rest } = payload.inputData;
      payload.inputData = {
        ...rest,
        type: material_type,
      };
      const responseData = id
        ? await saveRecords(payload, updateEndpoint)
        : await saveRecords(payload, createEndpoint);

      // Handle response
      if (responseData && responseData.code === 1) {
        toast.success(
          `${headerName} ${id ? "updated" : "saved"} successfully!`
        );
        navigate(navigatePage?.back);
      } else {
        toast.error(
          responseData.message || `Error while saving ${headerName}!`
        );
      }
    } catch (error) {
      console.error("Error during submission:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoadingData(false);
    }
  };

  const handleTypeSelect = (options) => {
    const selectedValue = options ? options.value : "";
    const fieldError = validate(validationType, {
      material_type: `${selectedValue}`,
    });

    setSelectedType(options);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      material_type: fieldError?.material_type || "",
    }));

    setFormData((prevState) => ({
      ...prevState,
      inputData: {
        ...prevState.inputData,
        material_type: selectedValue,
      },
    }));
  };

  useEffect(() => {
    if (formErrors?.name) {
      const updatedError = formErrors.name.replace(/\bName\b/, "Category Name");

      setCategoryNameError(updatedError)
    }
    else
      setCategoryNameError(null)
  }, [formErrors?.name]);

  useEffect(() => {
    if (formErrors?.material_type) {
      const updatedError = formErrors.material_type.replace(/\bMaterial type\b/, "Type");

      setMaterialTypeError(updatedError)
    }
    else
      setMaterialTypeError(null)
  }, [formErrors?.material_type]);

  return (
    <CustomForm
      title={id ? "UPDATE " + headerName : "CREATE " + headerName}
      loadingData={loadingData}
      onFormSubmit={handleSubmit}
      handleBackClick={() => navigate(navigatePage?.back)}
      id={id}
    >
      <Row className="mt-2">
        <Col md="6" className="mt-3">
          <SelectInput
            label="Type"
            options={typeOptions}
            value={selectedType}
            onSelect={handleTypeSelect}
            placeholder="Select Type"
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={materialTypeError}
          />
        </Col>

        <Col md="6" className="mt-3">
          <TextInput
            label="Category Name"
            name="name"
            value={formData?.inputData?.name}
            onChange={onInputChange}
            placeholder="Enter Category Name"
            inputClassName="form-control"
            labelClassName="form-label"
            asterisk={true}
            customError={categoryNameError}
          />
        </Col>
        <Col md="6" className="mt-3">
          <TextAreaInput
            label="Category Description"
            name="descriptions"
            value={formData?.inputData?.descriptions}
            onChange={onInputChange}
            placeholder="Enter Category Description"
            inputClassName="form-control"
            labelClassName="form-label"
            customError={formErrors?.descriptions}
          />
        </Col>
      </Row>

      {/* Add documents */}

      <Row className="mt-4">
        <Col lg="12">
          <CheckboxInput
            name="is_active"
            label="Is Active"
            value={formData?.inputData?.is_active}
            checked={formData?.inputData?.is_active}
            handleTextChange={onInputChange}
            parentClassName="form-check"
            checkBoxClassName="form-check-input"
            labelClassName="form-check-label"
          />
        </Col>
      </Row>
    </CustomForm>
  );
};

export default CategoryCreate;
